import { useState, useRef, useEffect, useCallback } from 'react';
import { useToast } from '../components/contexts/ToastContext';
import apiService from '../services/apiService';

/**
 * Hook para dictado por voz con transcripción server-side.
 * Graba audio via MediaRecorder, lo envía a apiService.transcribirAudio()
 * y entrega el texto transcrito via callback.
 *
 * @param {Function} onTranscript - Callback que recibe el texto transcrito
 * @returns {Object} Estado y acciones del dictado
 * @returns {boolean} returns.grabando - Si está grabando
 * @returns {boolean} returns.procesando - Si está transcribiendo
 * @returns {Function} returns.iniciar - Inicia grabación
 * @returns {Function} returns.detener - Detiene grabación
 * @returns {Function} returns.reset - Detiene y limpia estado
 */
function useDictation(onTranscript) {
  const { showToast } = useToast();
  const [grabando, setGrabando] = useState(false);
  const [procesando, setProcesando] = useState(false);
  const mediaRecorderRef = useRef(null);
  const chunksRef = useRef([]);
  const onTranscriptRef = useRef(onTranscript);
  onTranscriptRef.current = onTranscript;

  useEffect(() => {
    return () => {
      if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
        mediaRecorderRef.current.stop();
      }
    };
  }, []);

  const iniciar = useCallback(async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mediaRecorder = new MediaRecorder(stream);
      mediaRecorderRef.current = mediaRecorder;
      chunksRef.current = [];

      mediaRecorder.ondataavailable = (e) => {
        if (e.data.size > 0) chunksRef.current.push(e.data);
      };

      mediaRecorder.onstop = async () => {
        stream.getTracks().forEach(t => t.stop());
        setProcesando(true);
        try {
          const mimeType = mediaRecorder.mimeType || 'audio/webm';
          const ext = mimeType.includes('ogg') ? 'ogg' : mimeType.includes('mp4') ? 'mp4' : 'webm';
          const blob = new Blob(chunksRef.current, { type: mimeType });
          const formData = new FormData();
          formData.append('audio', blob, `nota.${ext}`);
          const response = await apiService.transcribirAudio(formData);
          if (response.success && response.text) {
            onTranscriptRef.current(response.text);
          } else {
            showToast('No se pudo transcribir el audio', 'error');
          }
        } catch {
          showToast('Error al transcribir. Escribe la descripción manualmente.', 'error');
        } finally {
          setProcesando(false);
        }
      };

      mediaRecorder.start();
      setGrabando(true);
    } catch {
      showToast('No se pudo acceder al micrófono', 'error');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const detener = useCallback(() => {
    if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
      mediaRecorderRef.current.stop();
    }
    setGrabando(false);
  }, []);

  const reset = useCallback(() => {
    detener();
    setProcesando(false);
  }, [detener]);

  return { grabando, procesando, iniciar, detener, reset };
}

export default useDictation;
